import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Box, Button, FormControlLabel, Switch } from '@mui/material';
import { useCallbacks } from 'shared/hooks/utils';
import { groups_for_device } from '../dialog';
import { UniversalDialog } from 'console/dialogs/universal';
import { GraphDialogGroupItem } from './additional-components/group-item';
import { isSmallUI } from 'shared/app/lib/choose-ui-type';
import { SettingCard } from 'z3k-settings/components/material-utils/settings-card/card';
import { Control } from 'console/shared/components/fields/control';
import { TextInputWrapper } from 'console/shared/components/fields/text';
import { useBooleanState } from 'shared/hooks/state/boolean';
import { StrictNumberInput } from 'console/shared/components/fields';
import { HelpIcon } from 'console/shared/components/fields/help-icon';
import { ControlWithoutValidation } from 'console/shared/components/fields/control/without-validation';
const min_height_line = 4;
const max_height_line = 18;
export const useGraphDialog = () => {
    const [opened, close, open] = useBooleanState(false);
    const [local_block, setBlock] = useState(undefined);
    const _open2 = (block) => {
        open();
        setBlock(block);
    };
    const _open = useCallbacks([open, setBlock]);
    const GraphDialog = useMemo(() => {
        const GraphDialogComponent = ({ device, onConfirm, onReject, ...props }) => {
            const [selectedSource, setSelectedSource] = useState({});
            const [title_block, setTitleBlock] = useState(local_block?._title_block);
            const [show_tooltip, setShowTooltip] = useState(local_block?.get_show_tooltip() ?? false);
            const [show_legend, setShowLegend] = useState(local_block?.get_show_legend() ?? true);
            const [bool_graph_on_main, setBoolGraphOnMain] = useState(local_block?.get_bool_graph_on_main() ?? true);
            const [height_line_boolean_graph, setHeightLineBoolenGraph] = useState(local_block?.get_size_boolean_graph() ?? 5);
            const _onConfirm = () => {
                onConfirm(selected_source_array(), local_block, title_block, show_tooltip, show_legend, height_line_boolean_graph, bool_graph_on_main);
                close();
            };
            const many_count_boolean_lines = useMemo(() => {
                if (local_block) {
                    return local_block.get_count_boolean_lines() > 1 ? true : false;
                }
                return false;
            }, [local_block]);
            // useEffect(() => {
            //     if (many_count_boolean_lines) {
            //         setBoolGraphOnMain(false);
            //     }
            // }, [many_count_boolean_lines]);
            const groups = groups_for_device(device);
            const psources = groups.map((group) => group.possible_sources(device));
            const get_name_source_for_selected = (source) => {
                if ('object_id' in source) {
                    return `${source.title()}-${source.object_id}`;
                }
                if ('zone_no' in source) {
                    return `${source.title()}-${source.zone_no}`;
                }
                return source.title();
            };
            const use_mobile_height = isSmallUI();
            const checkboxes = useMemo(() => {
                const Checkboxes = {};
                Object.entries(groups)
                    //@ts-ignore
                    .filter(([group_no, group]) => psources[group_no].length > 0)
                    .map(([group_no, group]) => {
                    const newGroupSelect = {};
                    //@ts-ignore
                    const items = psources[group_no].map((source, i) => {
                        const select_source = {
                            class: source,
                            checked: false,
                            color: undefined,
                        };
                        if (local_block) {
                            select_source.checked = local_block
                                .sources()
                                .some((existing) => source.is_same(existing));
                            const index = local_block
                                .sources()
                                .findIndex((el) => {
                                if (el.title() === source.title()) {
                                    if ('object_id' in source &&
                                        'object_id' in el) {
                                        return (source.object_id ===
                                            el.object_id);
                                    }
                                    return true;
                                }
                                return false;
                            });
                            if (index >= 0)
                                select_source.color =
                                    local_block.sources()[index]._color;
                        }
                        newGroupSelect[get_name_source_for_selected(source)] =
                            select_source;
                        return source;
                    });
                    Checkboxes[group.title(device)] = items;
                    setSelectedSource((prevState) => ({
                        ...prevState,
                        [group.title(device)]: newGroupSelect,
                    }));
                });
                return Checkboxes;
            }, [device]);
            const update_item_checkbox = useCallback((checked, name_item, group_name) => {
                setSelectedSource((prevState) => ({
                    ...prevState,
                    [group_name]: {
                        ...prevState[group_name],
                        [name_item]: {
                            ...prevState[group_name][name_item],
                            checked: checked,
                        },
                    },
                }));
            }, []);
            const update_item_color = useCallback((color, name_item, group_name) => {
                setSelectedSource((prevState) => ({
                    ...prevState,
                    [group_name]: {
                        ...prevState[group_name],
                        [name_item]: {
                            ...prevState[group_name][name_item],
                            color: color,
                        },
                    },
                }));
            }, []);
            const update_group_checkbox = useCallback((checked, group_name) => {
                const newGroup = { ...selectedSource[group_name] };
                Object.entries(newGroup).forEach(([item_name, item]) => (item.checked = checked));
                setSelectedSource({
                    ...selectedSource,
                    [group_name]: newGroup,
                });
            }, [selectedSource]);
            const change_title_block = useCallback((new_value) => {
                setTitleBlock(new_value);
            }, []);
            const selected_source_array = useCallback(() => {
                const result = [];
                const mandatory = groups.flatMap((group) => group.mandatory_sources(device));
                Object.entries(selectedSource).forEach(([group_name, items]) => {
                    Object.entries(items).forEach(([item_name, item]) => {
                        if (item.checked) {
                            item.class.set_color(item.color);
                            result.push(item.class);
                        }
                    });
                });
                result.push(...mandatory);
                return result;
            }, [selectedSource]);
            const onChangeTooltip = useCallback((e) => setShowTooltip(e.target.checked), []);
            const onChangeLegend = useCallback((e) => setShowLegend(e.target.checked), []);
            const onChangeBoolGraphOnMain = useCallback((e) => setBoolGraphOnMain(e.target.checked), []);
            const has_error = useMemo(() => {
                if (height_line_boolean_graph > max_height_line ||
                    height_line_boolean_graph < min_height_line) {
                    return `Допустимые значения от ${min_height_line} до ${max_height_line}`;
                }
                return null;
            }, [min_height_line, max_height_line, height_line_boolean_graph]);
            return (_jsx(UniversalDialog, { open: opened, onClose: close, title: 'Выберите графики', footer: _jsx(_Fragment, { children: _jsx(Button, { color: "primary", onClick: _onConfirm, variant: "contained", children: "\u041E\u043A" }) }), ...props, children: checkboxes && (_jsxs(Box, { display: "flex", flexDirection: "column", gap: 2, mt: 1, sx: { minWidth: use_mobile_height ? '' : '500px' }, children: [_jsxs(SettingCard, { title: 'Настройки карточки с графиками', container_props: {
                                gap: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                maxWidth: '95%',
                                ml: use_mobile_height ? 0 : 2,
                            }, children: [_jsx(ControlWithoutValidation, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", value: title_block ?? '', onChange: change_title_block, field: TextInputWrapper }), _jsx(FormControlLabel, { control: _jsx(Switch, { checked: show_tooltip, onChange: onChangeTooltip, color: "primary" }), label: "\u0412\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u043E\u0442\u043E\u0431\u0440\u0430\u0436\u0435\u043D\u0438\u0435 \u043B\u0435\u0433\u0435\u043D\u0434\u044B \u043F\u0440\u0438 \u043D\u0430\u0432\u0435\u0434\u0435\u043D\u0438\u0438 \u043D\u0430 \u0433\u0440\u0430\u0444\u0438\u043A" }), _jsx(FormControlLabel, { control: _jsx(Switch, { checked: show_legend, onChange: onChangeLegend, color: "primary" }), label: "\u0412\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u043E\u0442\u043E\u0431\u0440\u0430\u0436\u0435\u043D\u0438\u0435 \u0441\u0442\u0430\u0442\u0438\u0447\u043D\u043E\u0439 \u043B\u0435\u0433\u0435\u043D\u0434\u044B \u043F\u043E\u0434 \u0433\u0440\u0430\u0444\u0438\u043A\u043E\u043C" }), _jsxs(Box, { display: "flex", alignItems: "center", children: [_jsx(FormControlLabel, { control: _jsx(Switch, { checked: !many_count_boolean_lines &&
                                                    bool_graph_on_main, onChange: onChangeBoolGraphOnMain, color: "primary" }), disabled: many_count_boolean_lines, label: "\u041E\u0442\u043E\u0431\u0440\u0430\u0436\u0430\u0442\u044C \u043B\u043E\u0433\u0438\u0447\u0435\u0441\u043A\u043E\u0435 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u0435 \u0432\u043C\u0435\u0441\u0442\u0435 \u0441 \u043E\u0441\u043D\u043E\u0432\u043D\u044B\u043C" }), _jsx(HelpIcon, { help_id: "bool_graph_on_main" })] }), _jsx(Box, { width: "50%", children: _jsx(Control, { label: "\u0422\u043E\u043B\u0449\u0438\u043D\u0430 \u043B\u0438\u043D\u0438\u0439 \u043B\u043E\u0433\u0438\u0447\u0435\u0441\u043A\u0438\u0445 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u0439", validation_result: has_error, validation_warnings_result: null, value: height_line_boolean_graph, help_id: "size_boolean_graph", onChange: setHeightLineBoolenGraph, field: (input_props) => (_jsx(StrictNumberInput, { step: 0.5, min: min_height_line, max: max_height_line, disabled: !many_count_boolean_lines &&
                                                bool_graph_on_main, ...input_props })) }) })] }), Object.entries(checkboxes).map(([group, items]) => (_jsx(GraphDialogGroupItem, { items: items, group_name: group, selectedSource: selectedSource, update_item_color: update_item_color, update_item_checkbox: update_item_checkbox, get_name_source_for_selected: get_name_source_for_selected, update_group_checkbox: update_group_checkbox }, group)))] })) }));
        };
        return GraphDialogComponent;
    }, [opened, local_block, close]);
    return [GraphDialog, _open];
};
